import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    Chip,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import dayjs from 'dayjs';

import { shiftRowsToNow } from './seedDates';

import ChevronDownIcon from 'modules/common/components/Icons/ChevronDownIcon';
import NavigationSearchIcon from 'modules/common/components/Icons/NavigationSearchIcon';
import DownloadIcon from 'modules/common/components/Icons/DownloadIcon';
import PlusIcon from 'modules/common/components/Icons/PlusIcon';
import ChartBarIcon from 'modules/common/components/Icons/ChartBarIcon';

/**
 * Demo replacement for /bookkeeping/general-ledger. Prod renders a HurdlrIframe; the demo
 * rebuilds the journal-entry ledger natively with synthetic entries. All data is mocked
 * — accounts and descriptions are illustrative, and the seeded dates are moved forward so the
 * latest entry falls in the current month.
 */

type EntrySource = 'Invoice Payment' | 'Payout' | 'Expense' | 'Transfer';

interface LedgerAccount {
    id: string;
    name: string;
}

interface LedgerEntry {
    id: string;
    date: string; // ISO
    source: EntrySource;
    description: string;
    counterparty: string;
    /** Amount on this account. Positive = debit, negative = credit. */
    amount: number;
    /** The other side of the journal entry — shown in the Account column. */
    offsetAccountId: string;
}

const ACCOUNTS: LedgerAccount[] = [
    { id: '11002', name: 'Stripe' },
    { id: '11001', name: 'Wells Fargo Business *0378' },
    { id: '41000', name: 'Services Income' },
    { id: '42000', name: 'Consulting Revenue' },
    { id: '61000', name: 'Office Expense' },
    { id: '63000', name: 'Software & Subscriptions' },
    { id: '36000', name: 'Shareholder Distribution' },
];

const accountById = (id: string) => ACCOUNTS.find((a) => a.id === id);
const fmtAccount = (id: string) => {
    const a = accountById(id);
    return a ? `#${a.id} ${a.name}` : `#${id}`;
};

/**
 * Generates a mixed set of entries for the selected account. Each entry pairs the
 * selected account with an offset account: invoice payments come in (debit), payouts
 * go out (credit), expenses go out, transfers move between bank accounts.
 */
function generateEntries(accountId: string): LedgerEntry[] {
    if (accountId === '11002') {
        // Stripe bank account ledger — invoice payments come in, payouts go out
        return [
            {
                id: 'e_ly_001',
                date: '2025-11-12',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-088',
                counterparty: 'Stripe (CSG-088)',
                amount: 4200.0,
                offsetAccountId: '41000',
            },
            {
                id: 'e_ly_002',
                date: '2025-11-30',
                source: 'Payout',
                description: 'STRIPE TRANSFER ST-A1B2C3D4E5F6...',
                counterparty: 'Wells Fargo *0378',
                amount: -4200.0,
                offsetAccountId: '11001',
            },
            {
                id: 'e_ly_003',
                date: '2025-12-08',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-091',
                counterparty: 'Stripe (CSG-091)',
                amount: 1875.0,
                offsetAccountId: '41000',
            },
            {
                id: 'e_ly_004',
                date: '2025-12-22',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-094',
                counterparty: 'Stripe (CSG-094)',
                amount: 3320.5,
                offsetAccountId: '41000',
            },
            {
                id: 'e_ly_005',
                date: '2025-12-30',
                source: 'Payout',
                description: 'STRIPE TRANSFER ST-Z9Y8X7W6V5U4...',
                counterparty: 'Wells Fargo *0378',
                amount: -5195.5,
                offsetAccountId: '11001',
            },
            {
                id: 'e_001',
                date: '2026-03-24',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-102',
                counterparty: 'Stripe (CSG-102)',
                amount: 2930.18,
                offsetAccountId: '41000',
            },
            {
                id: 'e_002',
                date: '2026-03-26',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-103',
                counterparty: 'Stripe (CSG-103)',
                amount: 596.86,
                offsetAccountId: '41000',
            },
            {
                id: 'e_003',
                date: '2026-03-31',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-104',
                counterparty: 'Stripe (CSG-104)',
                amount: 1814.5,
                offsetAccountId: '41000',
            },
            {
                id: 'e_004',
                date: '2026-03-31',
                source: 'Payout',
                description: 'STRIPE TRANSFER ST-N9P4Z4W9T9K...',
                counterparty: 'Wells Fargo *0378',
                amount: -3527.04,
                offsetAccountId: '11001',
            },
            {
                id: 'e_005',
                date: '2026-04-02',
                source: 'Payout',
                description: 'STRIPE TRANSFER ST-E0L3R4U5W3M...',
                counterparty: 'Wells Fargo *0378',
                amount: -1814.5,
                offsetAccountId: '11001',
            },
            {
                id: 'e_006',
                date: '2026-04-20',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-105',
                counterparty: 'Stripe (CSG-105)',
                amount: 72.43,
                offsetAccountId: '41000',
            },
            {
                id: 'e_007',
                date: '2026-04-20',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-106',
                counterparty: 'Stripe (CSG-106)',
                amount: 36.06,
                offsetAccountId: '41000',
            },
            {
                id: 'e_008',
                date: '2026-04-20',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-107',
                counterparty: 'Stripe (CSG-107)',
                amount: 36.06,
                offsetAccountId: '41000',
            },
            {
                id: 'e_009',
                date: '2026-04-20',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-108',
                counterparty: 'Stripe (CSG-108)',
                amount: 72.43,
                offsetAccountId: '41000',
            },
            {
                id: 'e_010',
                date: '2026-04-22',
                source: 'Payout',
                description: 'STRIPE TRANSFER ST-N5Z3G0W2S8E...',
                counterparty: 'Wells Fargo *0378',
                amount: -216.98,
                offsetAccountId: '11001',
            },
            {
                id: 'e_011',
                date: '2026-04-29',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-109',
                counterparty: 'Stripe (CSG-109)',
                amount: 21.31,
                offsetAccountId: '41000',
            },
            {
                id: 'e_012',
                date: '2026-04-29',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-110',
                counterparty: 'Stripe (CSG-110)',
                amount: 42.93,
                offsetAccountId: '41000',
            },
            {
                id: 'e_013',
                date: '2026-04-29',
                source: 'Invoice Payment',
                description: 'Stripe Payment for CSG-111',
                counterparty: 'Stripe (CSG-111)',
                amount: 42.93,
                offsetAccountId: '41000',
            },
        ];
    }
    // Wells Fargo — operating expenses + incoming Stripe transfers
    if (accountId === '11001') {
        return [
            {
                id: 'wf_001',
                date: '2026-03-31',
                source: 'Payout',
                description: 'Incoming Stripe transfer',
                counterparty: 'Stripe *11002',
                amount: 3527.04,
                offsetAccountId: '11002',
            },
            {
                id: 'wf_002',
                date: '2026-04-02',
                source: 'Expense',
                description: 'WeWork — office membership',
                counterparty: 'WeWork',
                amount: -850,
                offsetAccountId: '61000',
            },
            {
                id: 'wf_003',
                date: '2026-04-02',
                source: 'Payout',
                description: 'Incoming Stripe transfer',
                counterparty: 'Stripe *11002',
                amount: 1814.5,
                offsetAccountId: '11002',
            },
            {
                id: 'wf_004',
                date: '2026-04-08',
                source: 'Expense',
                description: 'Amazon Web Services',
                counterparty: 'AWS',
                amount: -249.99,
                offsetAccountId: '63000',
            },
            {
                id: 'wf_005',
                date: '2026-04-15',
                source: 'Transfer',
                description: 'Reasonable comp — April payroll',
                counterparty: 'Gusto',
                amount: -7000,
                offsetAccountId: '36000',
            },
        ];
    }
    // Default — show consulting revenue ledger
    return [
        {
            id: 'srv_001',
            date: '2026-03-15',
            source: 'Invoice Payment',
            description: 'Acme Corp — March retainer',
            counterparty: 'Acme Corp',
            amount: 8500,
            offsetAccountId: '11002',
        },
        {
            id: 'srv_002',
            date: '2026-03-22',
            source: 'Invoice Payment',
            description: 'Westwood Studios — design',
            counterparty: 'Westwood Studios',
            amount: 7200,
            offsetAccountId: '11002',
        },
        {
            id: 'srv_003',
            date: '2026-04-12',
            source: 'Invoice Payment',
            description: 'Cohen & Lee LLP — invoice',
            counterparty: 'Cohen & Lee',
            amount: 6000,
            offsetAccountId: '11002',
        },
    ];
}

/* ----------------------------- dropdown atom ----------------------------- */

function FilterDropdown<T extends string>({
    label,
    value,
    options,
    onChange,
}: {
    label: string;
    value: T;
    options: { value: T; label: string }[];
    onChange: (v: T) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const current = options.find((o) => o.value === value);
    return (
        <>
            <Button
                variant="text"
                size="small"
                color="inherit"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                endIcon={<ChevronDownIcon fontSize="small" />}
                sx={{
                    color: 'text.primary',
                    fontWeight: 400,
                    px: 1,
                    py: 0.25,
                    minWidth: 0,
                    '&:hover': { bgcolor: 'action.hover' },
                }}>
                <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
                    {label}:
                </Typography>
                <Typography component="span" variant="body2">
                    {current?.label}
                </Typography>
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
                {options.map((o) => (
                    <MenuItem
                        key={o.value}
                        selected={o.value === value}
                        onClick={() => {
                            onChange(o.value);
                            setAnchorEl(null);
                        }}>
                        {o.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

/* ----------------------------- row + table ----------------------------- */

const SOURCE_COLORS: Record<EntrySource, string> = {
    'Invoice Payment': 'success.dark',
    Payout: 'success.dark',
    Expense: 'error.main',
    Transfer: 'primary.main',
};

const fmtMoney = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(
        Math.abs(value)
    );

function AccountChip({ accountId, highlighted }: { accountId: string; highlighted: boolean }) {
    return (
        <Stack direction="row" spacing={0.75} alignItems="center">
            <ChartBarIcon fontSize="small" sx={{ color: 'primary.main' }} />
            {highlighted ? (
                <Chip
                    label={fmtAccount(accountId)}
                    size="small"
                    sx={{
                        bgcolor: 'primary.lightHover',
                        color: 'primary.main',
                        fontWeight: 500,
                        borderRadius: 999,
                        height: 28,
                    }}
                />
            ) : (
                <Typography variant="body2" color="primary.main">
                    {fmtAccount(accountId)}
                </Typography>
            )}
        </Stack>
    );
}

function LedgerRow({
    entry,
    runningBalance,
    selectedAccountId,
}: {
    entry: LedgerEntry;
    runningBalance: number;
    selectedAccountId: string;
}) {
    const isDebit = entry.amount >= 0;
    // The Account column shows the offsetting account on the journal entry, except for
    // transfers between bank accounts where prod just labels the row with the selected
    // account itself (highlighted). Match that visual quirk.
    const accountColumnId =
        entry.source === 'Payout' && entry.offsetAccountId !== selectedAccountId
            ? selectedAccountId
            : entry.offsetAccountId;
    const accountHighlighted = accountColumnId === selectedAccountId;
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns:
                    '40px minmax(140px, 1fr) minmax(220px, 2fr) 110px 110px 110px minmax(220px, 1.5fr)',
                alignItems: 'center',
                px: 2,
                py: 1.75,
                borderTop: 1,
                borderColor: 'divider',
                columnGap: 2,
            }}>
            <Checkbox size="small" sx={{ p: 0.5 }} inputProps={{ 'aria-label': 'Select ledger row' }} />
            <Stack spacing={0.25}>
                <Typography variant="caption" sx={{ color: SOURCE_COLORS[entry.source], fontWeight: 600 }}>
                    {entry.source}
                </Typography>
                <Typography variant="body2">{dayjs(entry.date).format('MMM D, YYYY')}</Typography>
            </Stack>
            <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>
                    {entry.description}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                    {entry.counterparty}
                </Typography>
            </Stack>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {isDebit ? `+${fmtMoney(entry.amount)}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {!isDebit ? `+${fmtMoney(entry.amount)}` : ''}
            </Typography>
            <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {fmtMoney(runningBalance)}
            </Typography>
            <AccountChip accountId={accountColumnId} highlighted={accountHighlighted} />
        </Box>
    );
}

/* --------------------------------- shell --------------------------------- */

type DateRange = 'last12' | 'lastYear' | 'last90' | 'last30' | 'qtd';
const DATE_OPTIONS: { value: DateRange; label: string }[] = [
    { value: 'last12', label: 'Last 12 months' },
    { value: 'lastYear', label: 'Last year' },
    { value: 'last90', label: 'Last 90 days' },
    { value: 'last30', label: 'Last 30 days' },
    { value: 'qtd', label: 'Quarter to date' },
];

export default function DemoGeneralLedger() {
    const [accountId, setAccountId] = useState('11002');
    const [dateRange, setDateRange] = useState<DateRange>('last12');
    const [rows, setRows] = useState<'50' | '100' | '200'>('100');
    const [search, setSearch] = useState('');

    const entries = useMemo(() => shiftRowsToNow(generateEntries(accountId), ['date']), [accountId]);
    const filtered = useMemo(() => {
        const now = dayjs();
        const { rangeStart, rangeEnd } = (() => {
            switch (dateRange) {
                case 'last30':
                    return { rangeStart: now.subtract(30, 'day'), rangeEnd: now.endOf('day') };
                case 'last90':
                    return { rangeStart: now.subtract(90, 'day'), rangeEnd: now.endOf('day') };
                case 'qtd': {
                    const quarterStartMonth = Math.floor(now.month() / 3) * 3;
                    return { rangeStart: now.month(quarterStartMonth).startOf('month'), rangeEnd: now.endOf('day') };
                }
                case 'lastYear':
                    return {
                        rangeStart: now.subtract(1, 'year').startOf('year'),
                        rangeEnd: now.subtract(1, 'year').endOf('year'),
                    };
                case 'last12':
                default:
                    return { rangeStart: now.subtract(12, 'month').startOf('day'), rangeEnd: now.endOf('day') };
            }
        })();
        const q = search.trim().toLowerCase();
        return entries.filter((e) => {
            const entryDate = dayjs(e.date);
            if (entryDate.isBefore(rangeStart) || entryDate.isAfter(rangeEnd)) {
                return false;
            }
            if (!q) {
                return true;
            }
            return (
                e.description.toLowerCase().includes(q) ||
                e.counterparty.toLowerCase().includes(q) ||
                e.source.toLowerCase().includes(q)
            );
        });
    }, [entries, search, dateRange]);

    // Running balance per row, oldest first → newest, then reversed to display.
    const withBalance = useMemo(() => {
        const sorted = [...filtered].sort((a, b) => a.date.localeCompare(b.date));
        let bal = 0;
        return sorted.map((e) => {
            bal += e.amount;
            return { entry: e, balance: bal };
        });
    }, [filtered]);

    const visibleRows = useMemo(() => withBalance.slice(0, Number(rows)), [withBalance, rows]);

    const accountOptions = ACCOUNTS.map((a) => ({ value: a.id, label: `#${a.id} ${a.name}` }));

    return (
        <Box sx={{ width: '100%', maxWidth: 1440, mx: 'auto' }}>
            <Typography variant="h1" sx={{ mb: { xs: 4, md: 6 } }}>
                General Ledger
            </Typography>
            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, bgcolor: 'background.paper', p: 3 }}>
                <Stack
                    direction="row"
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 2, overflowX: 'auto', flexWrap: 'nowrap' }}>
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'nowrap', flexShrink: 0 }}>
                        <FilterDropdown
                            label="Account"
                            value={accountId}
                            options={accountOptions}
                            onChange={setAccountId}
                        />
                        <FilterDropdown
                            label="Date"
                            value={dateRange}
                            options={DATE_OPTIONS}
                            onChange={(v) => setDateRange(v)}
                        />
                        <FilterDropdown
                            label="Rows"
                            value={rows}
                            options={[
                                { value: '50', label: '50' },
                                { value: '100', label: '100' },
                                { value: '200', label: '200' },
                            ]}
                            onChange={(v) => setRows(v)}
                        />
                    </Stack>
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                        <TextField
                            size="small"
                            placeholder="Search Transactions"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <NavigationSearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ width: 220 }}
                        />
                        <Button
                            variant="outlined"
                            color="primary"
                            startIcon={<DownloadIcon fontSize="small" />}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Download
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            startIcon={<PlusIcon fontSize="small" />}
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                            Add Transaction
                        </Button>
                    </Stack>
                </Stack>

                <Box sx={{ borderTop: 1, borderColor: 'divider', mt: 1 }}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                '40px minmax(140px, 1fr) minmax(220px, 2fr) 110px 110px 110px minmax(220px, 1.5fr)',
                            alignItems: 'center',
                            px: 2,
                            py: 1.5,
                            columnGap: 2,
                        }}>
                        <Checkbox
                            size="small"
                            sx={{ p: 0.5 }}
                            inputProps={{ 'aria-label': 'Select all ledger rows' }}
                        />
                        <Typography variant="caption" fontWeight={700}>
                            Date
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                            Source
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                            Debit
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                            Credit
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                            Balance
                        </Typography>
                        <Typography variant="caption" fontWeight={700}>
                            Account
                        </Typography>
                    </Box>
                    {visibleRows.length === 0 ? (
                        <Box sx={{ py: 8, textAlign: 'center', borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="h6">No entries.</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Try a different filter or search term.
                            </Typography>
                        </Box>
                    ) : (
                        // Display oldest → newest top → bottom (matches the screenshot's ledger order)
                        visibleRows.map(({ entry, balance }) => (
                            <LedgerRow
                                key={entry.id}
                                entry={entry}
                                runningBalance={balance}
                                selectedAccountId={accountId}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Box>
    );
}
